/**
 * Zaznaczanie w trybie sekcji kartotek kokpitu decyzji.
 *
 * Stan zaznaczenia żyje w zakładce „Moje czekające" jako id KROKÓW decyzji (`stepInstanceId`) —
 * tak jak na zwykłej liście. Wiersz sekcji to jednak REKORD kartoteki, który ma kilka (zwykle jeden)
 * otwartych kroków, więc sekcja tłumaczy dwa światy na siebie:
 *  - rekord jest zaznaczony, gdy zaznaczone są WSZYSTKIE jego kroki (rekord bez kroków nigdy),
 *  - zaznaczenie rekordu zaznacza wszystkie jego kroki.
 * Każda tabela (sekcja albo tabela pozostałych kartotek) widzi i zmienia tylko SWÓJ wycinek
 * zaznaczenia, dlatego jej zmiana jest SCALANA z resztą, a nie podstawiana w całości — inaczej
 * „zaznacz wszystko" w jednej tabeli skasowałoby zaznaczenie w pozostałych.
 *
 * Rekord zaznaczony CZĘŚCIOWO (zaznaczona część jego kroków, np. po tym, jak do zaznaczonego rekordu
 * doszedł nowy krok czekający na użytkownika) nie ma w tabeli pustego-lub-pełnego pola wyboru, a
 * ukryty krok liczyłby się do licznika i akcji masowej. Dlatego sekcja po każdej zmianie wczytanych
 * danych zdejmuje z zaznaczenia kroki takich rekordów (`partiallyMarkedStepIds`); rekord zaznaczony w
 * całości zostaje zaznaczony.
 */

import type { MyPendingDecisionRow } from '@/lib/processes/approvals-api';

/** Wiersz sekcji w części, która interesuje zaznaczanie: id rekordu + jego otwarte kroki. */
export interface SelectableSectionRow {
  id: string;
  decisions: readonly MyPendingDecisionRow[];
}

/**
 * Co sekcja dostaje od zakładki, żeby uczestniczyć we wspólnym zaznaczeniu i wspólnej akcji masowej.
 * Brak całego obiektu = sekcja bez zaznaczania (np. zakres „Zespół", tylko podgląd).
 */
export interface DecisionSectionSelection {
  /**
   * Czy zaznaczanie jest teraz dozwolone (zakładka wyłącza je na danych zastępczych — jak na liście,
   * gdzie checkboxy znikają do czasu odpowiedzi).
   */
  enabled: boolean;
  /** Id kroków zaznaczonych na całym ekranie (wszystkie sekcje i tabela pozostałych kartotek). */
  markedStepIds: ReadonlySet<string>;
  /**
   * Podmienia zaznaczenie WYCINKA należącego do wołającej tabeli: z całego zaznaczenia znikają kroki
   * `ownStepIds` (wszystkie kroki, które ta tabela ma wczytane), a dochodzą `nextStepIds`.
   * Zaznaczenie pozostałych tabel zostaje bez zmian.
   */
  onSliceChange: (ownStepIds: readonly string[], nextStepIds: readonly string[]) => void;
  /**
   * Zgłasza zakładce kroki, które sekcja ma teraz wczytane i pokazane (do licznika, akcji masowej i
   * wstrzymania auto-odświeżania), albo `null`, gdy nie ma żadnych (zwinięcie, błąd, dane zastępcze).
   * Zakładka trzyma je pod kluczem sekcji; nowe zgłoszenie zastępuje poprzednie.
   */
  onLoadedStepsChange: (sectionKey: string, steps: readonly MyPendingDecisionRow[] | null) => void;
}

/** Id wszystkich kroków podanych wierszy (kolejność wierszy, potem kroków). */
export function stepIdsOfRows(rows: readonly SelectableSectionRow[]): string[] {
  return rows.flatMap((row) => row.decisions.map((decision) => decision.stepInstanceId));
}

/** Id rekordów, których WSZYSTKIE kroki są zaznaczone (rekord bez kroków nie jest zaznaczony). */
export function selectedRecordIds(
  rows: readonly SelectableSectionRow[],
  markedStepIds: ReadonlySet<string>,
): string[] {
  return rows
    .filter(
      (row) =>
        row.decisions.length > 0 &&
        row.decisions.every((decision) => markedStepIds.has(decision.stepInstanceId)),
    )
    .map((row) => row.id);
}

/** Id kroków wszystkich rekordów o podanych id (rekordy spoza `rows` pomijane). */
export function stepIdsOfRecords(
  rows: readonly SelectableSectionRow[],
  recordIds: readonly string[],
): string[] {
  const wanted = new Set(recordIds);
  return stepIdsOfRows(rows.filter((row) => wanted.has(row.id)));
}

/**
 * Id zaznaczonych kroków rekordów zaznaczonych CZĘŚCIOWO: rekord ma kilka kroków, a zaznaczona jest
 * tylko ich część (rekord bez kroków i rekord zaznaczony w całości lub wcale nie wchodzą). Takie
 * kroki sekcja zdejmuje z zaznaczenia, żeby pole wyboru rekordu, licznik i akcja masowa mówiły to samo.
 */
export function partiallyMarkedStepIds(
  rows: readonly SelectableSectionRow[],
  markedStepIds: ReadonlySet<string>,
): string[] {
  return rows.flatMap((row) => {
    const marked = row.decisions.filter((decision) => markedStepIds.has(decision.stepInstanceId));
    return marked.length > 0 && marked.length < row.decisions.length
      ? marked.map((decision) => decision.stepInstanceId)
      : [];
  });
}

/**
 * Kroki, które po zmianie zaznaczenia w tabeli (`recordIds` = id zaznaczonych rekordów z jej
 * `onSelectionChange`) mają stanowić wycinek tej tabeli: kroki zaznaczonych rekordów.
 *
 * Wyjątek — „zaznacz wszystko" z nagłówka tabeli. Tabela (wspólna) liczy wiersze zaznaczone po ich
 * id, a rekord bez kroków nigdy nie jest zaznaczony (`selectedRecordIds`), więc przy takim rekordzie
 * nagłówek nigdy nie pokazuje stanu „wszystko zaznaczone" i kolejny klik znów wysyła „zaznacz
 * wszystko". Gdy w tabeli są rekordy bez kroków, `recordIds` obejmuje wszystkie wiersze, a wszystkie
 * rekordy z krokami są już zaznaczone, taką zmianę traktujemy jako odznaczenie wszystkiego (wycinek
 * pusty). Tej samej zmiany nie da się odróżnić od kliknięcia pola samego rekordu bez kroków (taki
 * rekord nie powinien istnieć), więc takie kliknięcie też odznacza.
 */
export function nextSliceStepIds(
  rows: readonly SelectableSectionRow[],
  markedStepIds: ReadonlySet<string>,
  recordIds: readonly string[],
): string[] {
  const withSteps = rows.filter((row) => row.decisions.length > 0);
  if (withSteps.length > 0 && withSteps.length < rows.length) {
    const wanted = new Set(recordIds);
    const requestsAll = rows.every((row) => wanted.has(row.id));
    const allWithStepsMarked = selectedRecordIds(rows, markedStepIds).length === withSteps.length;
    if (requestsAll && allWithStepsMarked) return [];
  }
  return stepIdsOfRecords(rows, recordIds);
}

/**
 * Zaznaczenie po podmianie wycinka: kroki `ownIds` znikają z `marked`, a `nextIds` dochodzą na koniec.
 * Kroki spoza wycinka zachowują kolejność i nie są dotykane.
 */
export function replaceSelectionSlice(
  marked: readonly string[],
  ownIds: readonly string[],
  nextIds: readonly string[],
): string[] {
  const own = new Set(ownIds);
  const kept = marked.filter((id) => !own.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...Array.from(new Set(nextIds.filter((id) => !keptSet.has(id))))];
}
